// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../CalendarMonthPage/i18n.json';

import styled from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';
import {
  zIndex,
  spacing,
  fontWeight,
  fontSize,
  mediaMin,
  mediaMax,
  borderRadius,
} from 'presentations/views/styles';
import { white, black, gray01 } from 'presentations/views/styles/color';
import CalendarPageTemplate from 'presentations/views/components/templates/apps/CalendarPageTemplate';
import {
  CalendarListContainer,
  MonthContainer,
  MonthLink,
  MonthTitleContainer,
} from 'presentations/views/components/organisms/apps/CalendarList';
import MonthCalendar from 'presentations/views/components/organisms/apps/MonthCalendar';
import { CalendarArray } from 'usecases/app_calendar';
import { Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import Breadcrumb from 'presentations/routes/breadcrumb';
import Link from 'presentations/views/components/atoms/Link';
import {
  appCalendarMonthFilterPath,
  appCalendarMonthPath,
} from 'presentations/routes/path';
import { getDateTime } from 'infras/datetime';
import { imagePath } from 'presentations/assets';
import Head from 'next/head';
import FooterContentBanner from 'presentations/views/components/molecules/OwnedContentBanner/FooterContentBanner';

interface Props {
  mainCalendar: CalendarArray;
  calendars: CalendarArray[];
  calendarName: string;
  year: number;
  month: number;
  filter?: string;
  gengo: Gengo;
  routes?: Breadcrumb[];
  holidays: any;
  holidayMasters: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const date = getDateTime(
    { year: props.year, month: props.month },
    'Asia/Tokyo',
  ).startOf('month');
  const gengo = t('gengoTitleKana', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
    kana: props.gengo.kana,
  });
  const mainImage = `apps/calendar/mv/${props.month}.jpg`;

  const nextMonth = getDateTime({ year: props.year, month: props.month }).plus({
    month: 1,
  });
  const nextMonthUrl = props.filter
    ? appCalendarMonthFilterPath(nextMonth.year, nextMonth.month, props.filter)
    : appCalendarMonthPath(nextMonth.year, nextMonth.month);
  const title = t('monthTitle', {
    date: date.toJSDate(),
    calendarName: props.calendarName,
  });
  const image = imagePath(mainImage, { height: 280 });

  return (
    <CalendarPageTemplate
      routes={props.routes}
      filter={props.filter}
      month={props.month}
      year={props.year}
      holidayMasters={props.holidayMasters}
      holidays={props.holidays}
    >
      <Head>
        <link rel="preload" as="image" href={image} />
      </Head>
      <Cover>
        <CurrentMonthContainer>
          <CurrentMonthTitleContainer>
            <CurrentMonthTitle>
              {title}
              <PageDescription>{gengo}</PageDescription>
            </CurrentMonthTitle>
            <NextMonthLink href={nextMonthUrl}>{`${t('nextMonth', {
              date: nextMonth.toJSDate(),
            })}`}</NextMonthLink>
          </CurrentMonthTitleContainer>

          <ThemeProvider theme={{ appearance: 'dark' }}>
            <MonthCalendar
              weeks={props.mainCalendar}
              isShowToday={true}
              filter={props.filter}
            />
          </ThemeProvider>
        </CurrentMonthContainer>
      </Cover>
      {/* <StickyAdContainer>
        <StickyAdBanner
          productType={ProductType.Calendar}
          name="calendar"
          isHideClose
        />
      </StickyAdContainer> */}
      <MainContents>
        <Content>
          <CalendarListContainer>
            {props.calendars.map((weeks, i: number) => {
              const current = date.plus({ month: i + 1 });
              const title = current.toLocaleString({
                year: current.month === 1 ? 'numeric' : undefined,
                month: 'long',
              });
              let link = appCalendarMonthPath(current.year, current.month);
              if (props.filter) {
                link = appCalendarMonthFilterPath(
                  current.year,
                  current.month,
                  props.filter,
                );
              }

              return (
                <MonthContainer key={i}>
                  <MonthTitleContainer>
                    <MonthLink href={link}>{title}</MonthLink>
                  </MonthTitleContainer>
                  <MonthCalendar weeks={weeks} filter={props.filter} />
                </MonthContainer>
              );
            })}
          </CalendarListContainer>
        </Content>
      </MainContents>
    </CalendarPageTemplate>
  );
}

const MainContents = styled.main`
  padding: ${spacing(40)} ${spacing(16)};
  ${mediaMax.tablet} {
    padding: ${spacing(24)} 0;
  }
`;
const Content = styled.div`
  color: ${black};
  ${mediaMin.tablet} {
    max-width: 1100px;
    margin: 0 auto;
  }
`;

const Cover = styled.div`
  position: relative;
  background: #111111;
  overflow: hidden;
  border-radius: ${borderRadius(16)};
  ${mediaMax.desktop} {
    border-radius: 0;
  }
  ${mediaMin.tablet} {
    display: flex;
    flex-flow: row wrap;
  }
`;
const PageTitle = styled.p`
  color: ${white};
  font-size: ${fontSize(28)};
  font-weight: ${fontWeight('bold')};
  ${mediaMin.tablet} {
    font-size: 48px;
  }
`;
const PageTitleLink = styled(Link)`
  color: ${white};
`;
const PageDescription = styled.span`
  display: block;
  color: ${white};
  font-size: ${fontSize(13)};
  font-weight: 600;
  line-height: 1.6;
  ${mediaMin.tablet} {
    font-size: ${fontSize(16)};
  }
`;
const CurrentMonthContainer = styled.div`
  padding: 0 ${spacing(16)} ${spacing(16)};
  color: ${white};
  ${mediaMin.tablet} {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: ${spacing(24)};
    width: calc(100% / 12 * 5);
  }
  ${mediaMin.desktop} {
    padding: ${spacing(48)};
  }
`;
const CurrentMonthTitleContainer = styled.div`
  margin-bottom: ${spacing(32)};
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
    margin-top: ${spacing(24)};
    font-size: ${fontSize(18)};
  }
`;
const CurrentMonthTitle = styled.h1`
  color: ${white};
`;

const NextMonthLink = styled(Link)`
  display: block;
  color: ${white};
  border-radius: ${borderRadius(16)};
  background-color: ${gray01};
  padding: ${spacing(4)} ${spacing(16)};
  font-size: ${fontSize(14)};
`;

const StickyAdContainer = styled.div`
  position: sticky;
  top: 48px;
  left: 0;
  right: 0;
  ${mediaMin.tablet} {
    display: none;
  }
`;
const StickyAdBanner = styled(FooterContentBanner)`
  padding: ${spacing(12)} ${spacing(12)};
  ${mediaMax.tablet} {
    height: auto;
  }
`;
